// Release notes shown on the admin Releases page, newest first.
// When releasing: bump "version" in frontend/package.json and
// admin/frontend/package.json, then add an entry here with the same version.
// Change types: 'feature' | 'improvement' | 'fix' | 'security'.

const CHANGELOG = [
  {
    version: '1.13.0',
    date: '2026-09-27',
    title: 'Reset a forgotten password by SMS',
    changes: [
      { type: 'feature', text: 'Owners and staff who sign in with a mobile number can now reset a forgotten password themselves: "Forgot password?" sends a 6-digit code by SMS to their number. Email accounts still get the code by email.' },
      { type: 'security', text: 'An SMS code expires after 10 minutes and 5 wrong tries, and a number gets at most one code a minute. If the account also has an email, it is told whenever the password is reset by SMS.' },
    ],
  },
  {
    version: '1.12.0',
    date: '2026-09-26',
    title: 'See what sold and who bought it',
    changes: [
      { type: 'feature', text: 'Tapping Revenue on a business now starts with "What was sold": one line per product with how many sales, the quantity and the total (for example Honey 500g — P720). Tap a product to open it.' },
      { type: 'feature', text: 'Products in the Sales and Purchases tabs can now be tapped. The product page lists every customer who bought it (grouped, with how much they bought and anything they still owe), every individual sale, and every purchase with its supplier.' },
    ],
  },
  {
    version: '1.11.1',
    date: '2026-09-26',
    title: 'Fix: company pages said "Failed to load" when offline',
    changes: [
      { type: 'fix', text: 'With no signal, the Company Overview (the home screen for company owners) and the other company and financial pages showed "Failed to load company data". They now say you\'re offline, with shortcuts to the pages that work without signal (Sell, Inventory, Customers, Debtors, Transactions). Company Overview loads by itself when the signal returns.' },
    ],
  },
  {
    version: '1.11.0',
    date: '2026-09-26',
    title: 'Opens with no signal',
    changes: [
      { type: 'feature', text: 'ShopBook now opens even with no signal at all. After the first visit the app itself is kept on the phone, so Sell, Inventory, Customers, Debtors, Transactions, Expenses and Production work offline; what you record is saved on the phone and sent automatically when the signal returns.' },
      { type: 'improvement', text: 'When a new version is released, a "new version is ready" bar appears with a Reload button — the update never interrupts you mid-sale.' },
      { type: 'improvement', text: 'Offline, the Dashboard says so instead of showing empty totals, and it refreshes by itself when the connection returns.' },
      { type: 'improvement', text: 'On a very weak signal the app opens from your last session after a few seconds instead of staying on "Loading…".' },
      { type: 'security', text: 'Only the app\'s own files are kept for offline use — never your business records, which stay in the on-phone copy that is wiped when you log out.' },
    ],
  },
  {
    version: '1.10.4',
    date: '2026-09-26',
    title: 'Admin console: tidier sign-out area',
    changes: [
      { type: 'improvement', text: 'The admin name, Logout button and version number at the bottom of the sidebar now sit on separate lines instead of running together.' },
    ],
  },
  {
    version: '1.10.3',
    date: '2026-09-26',
    title: 'Admin console: safer sign-in sessions',
    changes: [
      { type: 'security', text: 'The admin console now keeps its sign-in in a secure cookie that the page\'s scripts cannot read, instead of in browser storage — the same protection the main app got in 1.10.0.' },
      { type: 'security', text: 'Every change made in the admin console is checked to come from the console itself, blocking requests forged by other websites.' },
      { type: 'security', text: 'Logging out now ends the session on the server straight away; before, a copied session stayed usable for up to 8 hours. Changing your password also retires the session it was made from.' },
      { type: 'improvement', text: 'On opening, the console asks the server whether you are still signed in, and returns you to the login page as soon as a session expires. Admins will need to sign in once after this update.' },
    ],
  },
  {
    version: '1.10.2',
    date: '2026-09-26',
    title: 'Fix: empty duplicate companies created on restart',
    changes: [
      { type: 'fix', text: 'A leftover start-up step created an empty duplicate company whenever a business was named differently from its group (every new sign-up), inflating the company count in the admin console. It now only runs for businesses without a group; the existing empty duplicates were removed.' },
    ],
  },
  {
    version: '1.10.1',
    date: '2026-09-26',
    title: 'Fix: terms acceptance box could not be ticked',
    changes: [
      { type: 'fix', text: 'On the "Before you start" screen the agree box stayed locked for some users. It now unlocks once both the Terms and the Privacy Policy have been opened, with a clear prompt for whichever is left and a link to read each in a full page.' },
    ],
  },
  {
    version: '1.10.0',
    date: '2026-09-26',
    title: 'Safer sign-in sessions',
    changes: [
      { type: 'security', text: 'Your sign-in is now kept in a secure cookie that the page\'s scripts cannot read, instead of in browser storage — a malicious script could no longer steal a session.' },
      { type: 'security', text: 'Every change you make is checked to come from the ShopBook app itself, blocking requests forged by other websites.' },
      { type: 'improvement', text: 'People already signed in stay signed in through the update — their session moves to the new cookie automatically.' },
    ],
  },
  {
    version: '1.9.0',
    date: '2026-09-26',
    title: 'Terms of Service and Privacy Policy',
    changes: [
      { type: 'feature', text: 'Terms of Service and Privacy Policy pages (/terms, /privacy), linked from the landing page and Settings.' },
      { type: 'feature', text: 'New sign-ups must tick that they have read and agree to both; existing users read and accept them once at next sign-in. The version and time accepted are recorded.' },
      { type: 'improvement', text: 'Sign-in and usage records older than 12 months are deleted automatically, as the Privacy Policy states.' },
      { type: 'improvement', text: 'The app version is now always shown under the account button, without opening the menu.' },
    ],
  },
  {
    version: '1.8.0',
    date: '2026-09-26',
    title: 'Complimentary accounts',
    changes: [
      { type: 'feature', text: 'Admins can make any company complimentary from its "Comp" button: choose the plan, an optional end date and a reason. Comped accounts are never billed, marked overdue, made read-only or auto-demoted.' },
      { type: 'feature', text: 'Complimentary accounts show a COMP badge in the admin panel, and every grant, change and end is recorded in the plan history.' },
      { type: 'improvement', text: 'Comped owners see "Complimentary — nothing to pay" instead of payment details, and a reminder in the last 7 days before a comp ends. When it ends, billing resumes with the usual 7-day grace period.' },
    ],
  },
  {
    version: '1.7.0',
    date: '2026-09-26',
    title: 'Correct numbers, voids and billing',
    changes: [
      { type: 'fix', text: 'Cash sales marked "Paid in full" now record the full amount received (they were recorded as P0). Existing sales are repaired when this version deploys.' },
      { type: 'fix', text: 'Invalid amounts (negative, blank or non-numbers) are refused everywhere, and totals are exact to the thebe.' },
      { type: 'feature', text: 'Owners and managers can void a sale or a payment, with a reason. Stock and money figures update automatically.' },
      { type: 'fix', text: 'Balance sheet balances: stock is valued at cost, each sale keeps the cost it had when sold, and cash is counted when it is received.' },
      { type: 'fix', text: 'Manufactured goods now carry the cost of their raw materials; reports use Botswana time for month boundaries.' },
      { type: 'fix', text: 'Overpayments are refused with the amount owed, instead of the extra disappearing.' },
      { type: 'security', text: 'Staff permissions now apply to offline sync too; "View financials" and "View customers" are enforced.' },
      { type: 'security', text: 'Records from one business can no longer be touched through another business’s account.' },
      { type: 'fix', text: 'Offline: sales keep the time they happened, production runs sync, refused records are shown instead of silently lost, and a weak signal no longer logs you out.' },
      { type: 'feature', text: 'Owners can reset a staff password; new staff must choose their own password at first sign-in. Owners can add a recovery email.' },
      { type: 'feature', text: 'Trial countdown, "How to pay" details with a payment reference, and in-app renewal for every plan (including Starter). Admin approvals email a receipt.' },
      { type: 'improvement', text: 'Unpaid accounts become read-only 7 days after the trial or subscription ends — data stays visible and exportable.' },
      { type: 'feature', text: 'CSV export of sales, payments, debtors, expenses, purchases, products and customers.' },
      { type: 'improvement', text: 'Company totals are no longer added up across different currencies.' },
      { type: 'improvement', text: 'Database changes are now versioned and owned by the main backend; the services can start in any order.' },
    ],
  },
  {
    version: '1.6.0',
    date: '2026-09-25',
    title: 'Version display and release notes',
    changes: [
      { type: 'feature', text: 'The app now shows its version in the account menu and on the landing page footer.' },
      { type: 'feature', text: 'Admin: new Releases tab listing what changed in each version; the running version is shown in the sidebar.' },
    ],
  },
  {
    version: '1.5.1',
    date: '2026-07-16',
    title: 'Security hardening',
    changes: [
      { type: 'security', text: 'Backends refuse to start with weak JWT secrets; database TLS certificate is pinned and the DB URL is kept out of files.' },
      { type: 'security', text: 'Both frontends send strict nginx security headers.' },
      { type: 'fix', text: 'Frontends re-resolve the backend address on every request, so backend redeploys no longer break login.' },
      { type: 'improvement', text: 'Free trials are signup-only; plan upgrades no longer include a trial.' },
      { type: 'fix', text: 'Top-of-screen UI stays clear of the iPhone notch / Dynamic Island.' },
      { type: 'fix', text: 'Landing page blur and selection styles now work in Safari.' },
    ],
  },
  {
    version: '1.5.0',
    date: '2026-07-13',
    title: 'Mobile number login',
    changes: [
      { type: 'feature', text: 'Users can register and log in with a mobile number as well as email.' },
      { type: 'improvement', text: 'Sidebar redesigned: a plan-independent rail with a Company hub and an account menu.' },
      { type: 'fix', text: 'iOS no longer leaves pages zoomed in after focusing an input.' },
    ],
  },
  {
    version: '1.4.0',
    date: '2026-07-08',
    title: 'Pricing, trials and engagement analytics',
    changes: [
      { type: 'feature', text: 'Starter plan repriced to P50/month, with 14-day free trials.' },
      { type: 'feature', text: 'Admin: user engagement analytics (logins, sessions, time on app).' },
      { type: 'feature', text: 'Admin: plan history in the company plan panel; trials can be re-granted.' },
      { type: 'feature', text: 'Enterprise business page supports walk-in sales and buyer autocomplete.' },
      { type: 'improvement', text: 'Service items are treated as services (no stock) in inventory.' },
      { type: 'improvement', text: 'Demo-data seeders for sales showcases, including a mechanic workshop.' },
      { type: 'security', text: 'Auth hardened: enumeration-safe register/reset, login lockout, cryptographically secure codes.' },
      { type: 'fix', text: 'Plan changes and their audit records are saved atomically; duplicate subscription activations are blocked.' },
      { type: 'fix', text: 'Admin backend fails fast when the database is unreachable.' },
    ],
  },
  {
    version: '1.3.0',
    date: '2026-07-05',
    title: 'Inventory, debtors and transactions',
    changes: [
      { type: 'feature', text: 'Transactions page: sales and purchases over a chosen date range.' },
      { type: 'feature', text: 'Opening stock on product creation, editable stock on hand, and "Own produce" zero-cost stock-in.' },
      { type: 'feature', text: 'Low-stock and "Depleted" flags in the inventory list.' },
      { type: 'feature', text: 'Customer history shows payments and outstanding balances; debtors open straight into history.' },
      { type: 'improvement', text: 'Product search on the sale form; overselling is blocked.' },
      { type: 'improvement', text: 'Thousand separators on all money values; dates shown as DD/MM/YYYY.' },
      { type: 'improvement', text: 'Fully paid walk-in sales no longer need a buyer name.' },
      { type: 'fix', text: 'Debts were double-counted; customer payments now settle the oldest debt first.' },
    ],
  },
  {
    version: '1.2.0',
    date: '2026-07-03',
    title: 'Offline and mobile',
    changes: [
      { type: 'feature', text: 'First-run onboarding welcome flow.' },
      { type: 'feature', text: 'Record Sale and the customer list work offline; walk-in buyers can be captured.' },
      { type: 'feature', text: 'Buyer-name typeahead links repeat customers.' },
      { type: 'improvement', text: 'New ShopBook app icons; the installed app opens straight to login.' },
      { type: 'improvement', text: 'Email is sent through Brevo, since Railway blocks SMTP.' },
      { type: 'fix', text: 'Several iOS layout fixes for the sidebar, hamburger and dashboard header.' },
    ],
  },
  {
    version: '1.1.0',
    date: '2026-06-30',
    title: 'Plans, upgrades and charts',
    changes: [
      { type: 'feature', text: 'Plan upgrade flow for users, with upgrade requests handled in the admin panel.' },
      { type: 'feature', text: 'Charts: visual financial reports for Pro and Enterprise.' },
      { type: 'feature', text: 'Admin: clickable Overview stat cards; empty deactivated accounts can be permanently deleted.' },
      { type: 'improvement', text: 'Product catalog available on every plan; signup asks to confirm the password.' },
      { type: 'fix', text: 'Railway deployment fixes: IPv6 binding, HTTPS redirect loop, and a migration crash on fresh databases.' },
    ],
  },
  {
    version: '1.0.0',
    date: '2026-06-26',
    title: 'Initial release',
    changes: [
      { type: 'feature', text: 'ShopBook core: businesses, products, sales, expenses and customers.' },
      { type: 'feature', text: 'Company management and a billing cron job.' },
      { type: 'feature', text: 'Admin panel with password reset, a breach-checked password policy and rate limiting.' },
      { type: 'improvement', text: 'Railway deployment and getshopbook.com email configuration.' },
    ],
  },
];

export default CHANGELOG;
